import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { AppButton, AppPillSwitch, AppQuantityStepper, AppSelect } from '../../ui';

const PAGE_COUNT_KEYS = [
  'NBPagesFourniesParVosSoins',
  'NbPagesQualitativesSEO',
  'NbPagesQualitativesCommercial',
  'NbPagesSimples',
  'NbPagesCatalogue',
];

const SECTION_ICONS = {
  type: 'lucide:layout-template',
  infra: 'lucide:server',
  modules: 'lucide:sparkles',
  contenus: 'lucide:files',
  saisie: 'lucide:keyboard',
  visuels: 'lucide:image',
  ajustements: 'lucide:pencil-ruler',
  langues: 'lucide:languages',
  abonnements: 'lucide:repeat',
};

const OPTION_LABELS = {
  Nouveau: 'Nouveau site',
};

const WIZARD_STEPS = [
  {
    id: 'projet',
    title: 'Le site',
    subtitle: 'Projet, hébergement, options',
    intro: 'Type de projet, hébergement et options utiles.',
    sections: ['type', 'infra', 'modules'],
  },
  {
    id: 'contenus',
    title: 'Les contenus',
    subtitle: 'Pages, textes, photos',
    intro: 'Combien de pages, qui saisit les contenus et d’où viennent les photos.',
    sections: ['contenus', 'saisie', 'visuels'],
  },
  {
    id: 'options',
    title: 'Finitions',
    subtitle: 'Ajustements, langues, contrats',
    intro: 'Derniers réglages : retouches incluses, langues étrangères et abonnements.',
    sections: ['ajustements', 'langues', 'abonnements'],
  },
];

const isFieldVisible = (field, wip) => {
  if ((field.key === 'TraductionFournies' || field.key === 'TraductionsIntragtionClient')
    && Number(wip?.TraductionNbLangues) <= 0) {
    return false;
  }
  if (field.key === 'TailleWorkspaceGo' && wip?.TypeMessagerieEmails !== 'Gmail') {
    return false;
  }
  const visibleWhen = field.visible_when;
  if (!visibleWhen?.field) return true;
  return String(wip?.[visibleWhen.field] ?? '') === String(visibleWhen.equals ?? '');
};

const optionLabel = (option) => {
  if (option && typeof option === 'object') return option.label;
  return OPTION_LABELS[option] || option;
};

const optionValue = (option) => (option && typeof option === 'object' ? option.value : option);

const ChiffrageWipForm = ({
  meta,
  wip,
  onChange,
  wizardStep,
  onWizardStepChange,
  disabled = false,
}) => {
  const [internalStep, setInternalStep] = useState(0);
  const isStepControlled = typeof wizardStep === 'number';
  const currentStep = isStepControlled ? wizardStep : internalStep;

  const setCurrentStep = (updater) => {
    const next = typeof updater === 'function' ? updater(currentStep) : updater;
    if (isStepControlled) {
      onWizardStepChange?.(next);
    } else {
      setInternalStep(next);
    }
  };

  const fieldsBySection = useMemo(() => {
    const grouped = {};
    (meta?.wip_fields ?? []).forEach((field) => {
      const section = field.section || 'autre';
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(field);
    });
    return grouped;
  }, [meta]);

  const totalPages = useMemo(() => (
    PAGE_COUNT_KEYS.reduce((sum, key) => sum + (Number(wip?.[key]) || 0), 0)
  ), [wip]);

  const step = WIZARD_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === WIZARD_STEPS.length - 1;

  const handleFieldChange = (key, value) => {
    const next = { ...wip, [key]: value };
    if (key === 'TypeDeSite') {
      next.TransfertNomDeDomaine = value === 'Refonte' ? 'Oui' : 'Non';
    }
    onChange(next);
  };

  const renderControl = (field) => {
    const value = wip?.[field.key];
    if (field.type === 'boolean') {
      const on = !!value;
      return (
        <button
          type="button"
          className="chiffrage-toggle"
          aria-pressed={on}
          disabled={disabled}
          onClick={() => handleFieldChange(field.key, !on)}
        >
          {on ? 'Oui' : 'Non'}
          <span className={`chiffrage-toggle__track${on ? ' is-on' : ''}`}>
            <span className="chiffrage-toggle__knob" />
          </span>
        </button>
      );
    }

    if (field.type === 'number') {
      return (
        <AppQuantityStepper
          value={Number(value) || 0}
          ariaLabel={field.label}
          disabled={disabled}
          onChange={(next) => handleFieldChange(field.key, next)}
        />
      );
    }

    const options = (field.options ?? []).map((option) => ({
      value: String(optionValue(option)),
      label: optionLabel(option),
    }));
    const useSelect = options.length > 3 || options.some((option) => option.label.length > 32);

    if (useSelect) {
      return (
        <AppSelect
          className="chiffrage-select"
          aria-label={field.label}
          disabled={disabled}
          value={String(value ?? options[0]?.value ?? '')}
          onChange={(event) => handleFieldChange(field.key, event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </AppSelect>
      );
    }

    return (
      <AppPillSwitch
        className="chiffrage-pills"
        variant="segment"
        ariaLabel={field.label}
        disabled={disabled}
        value={String(value ?? options[0]?.value ?? '')}
        options={options}
        onChange={(next) => handleFieldChange(field.key, next)}
      />
    );
  };

  const renderSection = (sectionKey) => {
    const fields = (fieldsBySection[sectionKey] ?? []).filter((field) => isFieldVisible(field, wip));
    if (fields.length === 0) return null;
    const sectionLabel = meta?.wip_sections?.[sectionKey] ?? sectionKey;

    return (
      <section key={sectionKey} className="chiffrage-block">
        <h3 className="chiffrage-block__title">
          <Icon icon={SECTION_ICONS[sectionKey] || 'lucide:circle'} width={17} />
          {sectionLabel}
        </h3>
        {fields.map((field) => (
          <div key={field.key} className="chiffrage-row">
            <div className="chiffrage-row__copy">
              <span className="chiffrage-row__label">{field.label}</span>
              {field.help ? <span className="chiffrage-row__help">{field.help}</span> : null}
            </div>
            {renderControl(field)}
          </div>
        ))}
        {sectionKey === 'contenus' ? (
          <div className="chiffrage-note">
            <Icon icon="lucide:sigma" width={16} />
            {totalPages} page{totalPages > 1 ? 's' : ''} au total (hors fiches catalogue)
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <div className="chiffrage-wizard">
      <div className="chiffrage-wizard__steps" role="tablist" aria-label="Étapes du chiffrage">
        {WIZARD_STEPS.map((item, index) => {
          const isActive = index === currentStep;
          const isDone = index < currentStep;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`chiffrage-wizard__step${isActive ? ' is-active' : ''}`}
              onClick={() => setCurrentStep(index)}
            >
              <span className={`chiffrage-wizard__mark${isDone ? ' is-done' : ''}${isActive ? ' is-active' : ''}`}>
                {isDone ? '✓' : index + 1}
              </span>
              <span className="chiffrage-wizard__step-copy">
                <span className="chiffrage-wizard__step-title">{item.title}</span>
                <span className="chiffrage-wizard__step-sub">{item.subtitle}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="chiffrage-wizard__body">
        <div className="chiffrage-wizard__intro">
          <span className="chiffrage-wizard__count">Étape {currentStep + 1} sur 3</span>
          <h2>{step.title}</h2>
          <p>{step.intro}</p>
        </div>
        {step.sections.map(renderSection)}
      </div>

      <div className="chiffrage-wizard__footer">
        <AppButton
          variant="outline-secondary"
          className="chiffrage-nav"
          icon="lucide:arrow-left"
          disabled={isFirst || disabled}
          onClick={() => setCurrentStep((value) => value - 1)}
        >
          Précédent
        </AppButton>
        {isLast ? (
          <AppButton
            variant="secondary"
            className="chiffrage-nav chiffrage-nav--dark"
            icon="lucide:rotate-ccw"
            disabled={disabled}
            onClick={() => setCurrentStep(0)}
          >
            Revoir depuis le début
          </AppButton>
        ) : (
          <AppButton
            variant="secondary"
            className="chiffrage-nav chiffrage-nav--dark"
            disabled={disabled}
            onClick={() => setCurrentStep((value) => value + 1)}
          >
            Étape suivante : {WIZARD_STEPS[currentStep + 1].title}
            <Icon icon="lucide:arrow-right" width={16} className="ms-1" />
          </AppButton>
        )}
      </div>
    </div>
  );
};

export default ChiffrageWipForm;
